/**
 * Admin discounts API client -- phase 6b, Task 1.
 *
 * One function per route in docs/features/discount-engine/ADMIN_API.md, all
 * over the shared `authInstance` (JWT attach + refresh interceptors live
 * there) and each returning the response `data` typed from types.ts. Screens
 * never call axios directly; tests `vi.mock('../api')` this module.
 *
 * `qk` is the react-query key factory for the area: hierarchical so a write
 * can invalidate `qk.all` (everything), `['discounts','promotions']` (every
 * promotions list) or one detail.
 */
import { authInstance } from '../../services/httpClient';
import type {
  AssignVoucherBody,
  BulkGenerateBody,
  BulkGenerateResponse,
  CreatePromotionBody,
  CreateVoucherBody,
  CustomerOverview,
  LedgerRow,
  ListLedgerQuery,
  ListPromotionsQuery,
  ListRedemptionsQuery,
  ListVouchersQuery,
  LoyaltyConfig,
  LoyaltyConfigBody,
  Paged,
  PointsAdjustmentBody,
  PointsAdjustmentResponse,
  PromotionDetail,
  PromotionRow,
  PutTiersBody,
  RedemptionRow,
  ReferralConfig,
  ReferralConfigBody,
  StatsResponse,
  TiersResponse,
  UpdatePromotionBody,
  VoucherRow,
} from './types';

const BASE = '/admin/discounts';

/** Drop undefined entries so axios never serialises `?kind=undefined`. */
const params = <T extends object>(q?: T): Partial<T> => {
  const out: Partial<T> = {};
  if (!q) return out;
  (Object.keys(q) as Array<keyof T>).forEach((k) => {
    if (q[k] !== undefined) out[k] = q[k];
  });
  return out;
};

export const discountsApi = {
  // --- Dashboard ---------------------------------------------------------
  stats: async (): Promise<StatsResponse> => (await authInstance.get<StatsResponse>(`${BASE}/stats`)).data,

  // --- Promotions --------------------------------------------------------
  listPromotions: async (q?: ListPromotionsQuery): Promise<Paged<PromotionRow>> =>
    (await authInstance.get<Paged<PromotionRow>>(`${BASE}/promotions`, { params: params(q) })).data,
  getPromotion: async (id: string): Promise<PromotionDetail> =>
    (await authInstance.get<PromotionDetail>(`${BASE}/promotions/${id}`)).data,
  createPromotion: async (body: CreatePromotionBody): Promise<PromotionDetail> =>
    (await authInstance.post<PromotionDetail>(`${BASE}/promotions`, body)).data,
  updatePromotion: async (id: string, body: UpdatePromotionBody): Promise<PromotionDetail> =>
    (await authInstance.put<PromotionDetail>(`${BASE}/promotions/${id}`, body)).data,
  setPromotionActive: async (id: string, active: boolean): Promise<PromotionDetail> =>
    (await authInstance.post<PromotionDetail>(`${BASE}/promotions/${id}/${active ? 'activate' : 'deactivate'}`)).data,
  deletePromotion: async (id: string): Promise<void> => {
    await authInstance.delete(`${BASE}/promotions/${id}`);
  },

  // --- Vouchers (codes) --------------------------------------------------
  listVouchers: async (promotionId: string, q?: ListVouchersQuery): Promise<Paged<VoucherRow>> =>
    (await authInstance.get<Paged<VoucherRow>>(`${BASE}/promotions/${promotionId}/vouchers`, { params: params(q) })).data,
  createVoucher: async (promotionId: string, body: CreateVoucherBody): Promise<VoucherRow> =>
    (await authInstance.post<VoucherRow>(`${BASE}/promotions/${promotionId}/vouchers`, body)).data,
  bulkGenerate: async (promotionId: string, body: BulkGenerateBody): Promise<BulkGenerateResponse> =>
    (await authInstance.post<BulkGenerateResponse>(`${BASE}/promotions/${promotionId}/vouchers/bulk`, body)).data,
  assignVoucher: async (id: string, body: AssignVoucherBody): Promise<VoucherRow> =>
    (await authInstance.put<VoucherRow>(`${BASE}/vouchers/${id}/assignment`, body)).data,
  setVoucherActive: async (id: string, active: boolean): Promise<VoucherRow> =>
    (await authInstance.post<VoucherRow>(`${BASE}/vouchers/${id}/${active ? 'activate' : 'deactivate'}`)).data,
  setVoucherExpiry: async (id: string, expiresAt: string | null): Promise<VoucherRow> =>
    (await authInstance.put<VoucherRow>(`${BASE}/vouchers/${id}/expiry`, { expiresAt })).data,

  // --- Redemptions & customers ------------------------------------------
  listRedemptions: async (q?: ListRedemptionsQuery): Promise<Paged<RedemptionRow>> =>
    (await authInstance.get<Paged<RedemptionRow>>(`${BASE}/redemptions`, { params: params(q) })).data,
  customerOverview: async (id: string): Promise<CustomerOverview> =>
    (await authInstance.get<CustomerOverview>(`${BASE}/customers/${id}`)).data,
  customerLedger: async (id: string, q?: ListLedgerQuery): Promise<Paged<LedgerRow>> =>
    (await authInstance.get<Paged<LedgerRow>>(`${BASE}/customers/${id}/ledger`, { params: params(q) })).data,
  adjustPoints: async (id: string, body: PointsAdjustmentBody): Promise<PointsAdjustmentResponse> =>
    (await authInstance.post<PointsAdjustmentResponse>(`${BASE}/customers/${id}/points-adjustment`, body)).data,

  // --- Loyalty -----------------------------------------------------------
  loyaltyConfig: async (): Promise<LoyaltyConfig> => (await authInstance.get<LoyaltyConfig>(`${BASE}/loyalty/config`)).data,
  putLoyaltyConfig: async (body: LoyaltyConfigBody): Promise<LoyaltyConfig> =>
    (await authInstance.put<LoyaltyConfig>(`${BASE}/loyalty/config`, body)).data,
  tiers: async (): Promise<TiersResponse> => (await authInstance.get<TiersResponse>(`${BASE}/loyalty/tiers`)).data,
  putTiers: async (body: PutTiersBody): Promise<TiersResponse> =>
    (await authInstance.put<TiersResponse>(`${BASE}/loyalty/tiers`, body)).data,

  // --- Referrals ---------------------------------------------------------
  referralConfig: async (): Promise<ReferralConfig> =>
    (await authInstance.get<ReferralConfig>(`${BASE}/referrals/config`)).data,
  putReferralConfig: async (body: ReferralConfigBody): Promise<ReferralConfig> =>
    (await authInstance.put<ReferralConfig>(`${BASE}/referrals/config`, body)).data,
};

export type DiscountsApi = typeof discountsApi;

/** react-query keys. Lists carry their query object so distinct filters cache separately. */
// NOTE (Task 1 review): `qk.promotion(id)` is a SEPARATE key from `qk.promotions(...)`, and
// `qk.vouchers(pid, …)` does not nest under either. A write that changes a promotion's
// counts (creating/deactivating a voucher) must invalidate BOTH `qk.promotion(id)` and the
// promotions list prefix; simplest is `queryClient.invalidateQueries({ queryKey: ['discounts'] })`.
export const qk = {
  all: ['discounts'] as const,
  stats: ['discounts', 'stats'] as const,
  promotions: (q?: ListPromotionsQuery) => ['discounts', 'promotions', q ?? {}] as const,
  promotion: (id: string) => ['discounts', 'promotion', id] as const,
  vouchers: (promotionId: string, q?: ListVouchersQuery) => ['discounts', 'vouchers', promotionId, q ?? {}] as const,
  redemptions: (q?: ListRedemptionsQuery) => ['discounts', 'redemptions', q ?? {}] as const,
  customer: (id: string) => ['discounts', 'customer', id] as const,
  ledger: (id: string, q?: ListLedgerQuery) => ['discounts', 'ledger', id, q ?? {}] as const,
  loyaltyConfig: ['discounts', 'loyalty', 'config'] as const,
  tiers: ['discounts', 'loyalty', 'tiers'] as const,
  referralConfig: ['discounts', 'referrals', 'config'] as const,
};
